import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react'
import { useOnboarding } from './useOnboarding'

export interface ForwardActionInput {
  label: ReactNode
  onClick: () => void | Promise<void>
  disabled?: boolean
}

/**
 * Register a forward-action button with the current OnboardingProvider.
 * The latest onClick is read through a ref so a new closure each render
 * does not re-register the action (which would loop).
 */
export function useForwardAction(action: ForwardActionInput | null) {
  const { setForwardAction } = useOnboarding()
  const handlerRef = useRef<(() => void | Promise<void>) | null>(null)

  // Keep ref in sync with latest closure. useLayoutEffect runs after render
  // but before paint / user interaction, so the handler is always current
  // when the forward-action button fires.
  useLayoutEffect(() => {
    handlerRef.current = action?.onClick ?? null
  })

  const label = action?.label
  const disabled = action?.disabled ?? false
  const isActive = action !== null

  useEffect(() => {
    if (!isActive) {
      setForwardAction(null)
      return
    }
    setForwardAction({
      label,
      disabled,
      onClick: () => handlerRef.current?.(),
    })
    return () => setForwardAction(null)
  }, [isActive, label, disabled, setForwardAction])
}
